import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { GoogleMap, MapAdvancedMarker, MapCircle, MapInfoWindow } from '@angular/google-maps';
import { CaregiverCardComponent } from 'src/app/requests-handling/caregiver-scoreboard/caregiver-card/caregiver-card.component';
import { GoogleMapsService } from 'src/app/requests-handling/caregiver-scoreboard/caregiver-selection/caregiver-selection-service/google-maps.service';
import { CaregiverSelectionRecordSortable } from 'src/app/shared/models/caregiverSelectionRecordSortable.models';
import { NearbyActiveFamilyMarker } from 'src/app/shared/models/nearby-active-family-marker.model';

interface CaregiverMapMarker {
  record: CaregiverSelectionRecordSortable;
  position: google.maps.LatLngLiteral;
  pin: google.maps.marker.PinElement | null;
}

interface FamilyMapMarker {
  family: NearbyActiveFamilyMarker;
  position: google.maps.LatLngLiteral;
  pin: google.maps.marker.PinElement | null;
}

// mapId condiviso con app-map (src/app/shared/components/map/map), già configurato su Google Cloud.
const ACTIVE_SEARCH_MAP_ID = 'fef0a1befdd8ed40';
const CAREGIVER_PIN_FROM = { r: 0xc0, g: 0x39, b: 0x2b }; // rosso, fitting index basso
const CAREGIVER_PIN_TO = { r: 0x27, g: 0xae, b: 0x60 }; // verde, fitting index alto
const FAMILY_PIN_COLOR = '#8e44ad';
const CENTER_PIN_COLOR = '#f1c40f'; // giallo oro: pin "casa" della richiesta principale
const ALREADY_SELECTED_PIN_COLOR = '#8a8f98'; // grigio neutro: già in candidatura, non è un risultato nuovo

@Component({
  selector: 'app-active-search-map',
  standalone: true,
  templateUrl: './active-search-map.component.html',
  styleUrls: ['./active-search-map.component.scss'],
  imports: [CommonModule, GoogleMap, MapAdvancedMarker, MapCircle, MapInfoWindow, CaregiverCardComponent],
})
export class ActiveSearchMapComponent implements OnChanges {
  @Input() centerCoordinates: google.maps.LatLngLiteral | null = null;
  @Input() rangeMeters = 0;
  @Input() caregiverRecords: CaregiverSelectionRecordSortable[] = [];
  @Input() alreadySelectedRecords: CaregiverSelectionRecordSortable[] = [];
  @Input() familyMarkers: NearbyActiveFamilyMarker[] = [];
  @Input() isAddingCaregiver = true;
  @Output() selectedCaregiver = new EventEmitter<CaregiverSelectionRecordSortable>();
  @Output() openFamilyRequest = new EventEmitter<NearbyActiveFamilyMarker>();

  mapOptions: google.maps.MapOptions | null = null;
  circleOptions: google.maps.CircleOptions | null = null;
  centerMarkerPin: google.maps.marker.PinElement | null = null;
  // maxWidth esplicito: senza, l'InfoWindow di Google a volte misura il contenuto prima che lo
  // stile "cardGold" (match >=90%, applicato via ngClass) sia completamente renderizzato, e lo
  // clippa. Riferimento stabile (non un literal inline nel template) per non ricrearlo ad ogni CD.
  readonly caregiverInfoWindowOptions: google.maps.InfoWindowOptions = { maxWidth: 380 };
  readonly familyInfoWindowOptions: google.maps.InfoWindowOptions = { maxWidth: 280 };
  caregiverMapMarkers: CaregiverMapMarker[] = [];
  alreadySelectedMapMarkers: CaregiverMapMarker[] = [];
  familyMapMarkers: FamilyMapMarker[] = [];

  private pinElementCtor: typeof google.maps.marker.PinElement | null = null;
  // Firme dell'ultimo build: proteggono da un genitore che ripassa un nuovo riferimento di
  // array con lo stesso contenuto ad ogni change detection (es. da una getter) — senza questa
  // guardia si ricostruirebbero i PinElement (web component nativi, costosi) ad ogni tick.
  private lastCaregiverSignature = '';
  private lastAlreadySelectedSignature = '';
  private lastFamilySignature = '';
  private lastCenterSignature = '';

  constructor(private googleMapsService: GoogleMapsService) {
    this.googleMapsService.getGoogleMaps().then(async (googleNamespace) => {
      const markerLibrary = (await googleNamespace.maps.importLibrary(
        'marker'
      )) as google.maps.MarkerLibrary;
      this.pinElementCtor = markerLibrary.PinElement;
      this.caregiverMapMarkers = this.buildCaregiverMarkers(this.caregiverRecords);
      this.alreadySelectedMapMarkers = this.buildAlreadySelectedMarkers(this.alreadySelectedRecords);
      this.familyMapMarkers = this.buildFamilyMarkers(this.familyMarkers);
      this.refreshCenterPin();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['centerCoordinates'] || changes['rangeMeters']) {
      this.updateMapAndCircleOptions();
      this.refreshCenterPin();
    }
    if (changes['caregiverRecords']) {
      const signature = this.caregiverSignature(this.caregiverRecords);
      if (signature !== this.lastCaregiverSignature) {
        this.lastCaregiverSignature = signature;
        this.caregiverMapMarkers = this.buildCaregiverMarkers(this.caregiverRecords);
      }
    }
    if (changes['alreadySelectedRecords']) {
      const signature = this.caregiverSignature(this.alreadySelectedRecords);
      if (signature !== this.lastAlreadySelectedSignature) {
        this.lastAlreadySelectedSignature = signature;
        this.alreadySelectedMapMarkers = this.buildAlreadySelectedMarkers(this.alreadySelectedRecords);
      }
    }
    if (changes['familyMarkers']) {
      const signature = this.familySignature(this.familyMarkers);
      if (signature !== this.lastFamilySignature) {
        this.lastFamilySignature = signature;
        this.familyMapMarkers = this.buildFamilyMarkers(this.familyMarkers);
      }
    }
  }

  private caregiverSignature(records: CaregiverSelectionRecordSortable[]): string {
    return (records ?? [])
      .map((record) => `${this.caregiverDocRef(record)}:${record.fittingIndex}`)
      .join('|');
  }

  private familySignature(families: NearbyActiveFamilyMarker[]): string {
    return (families ?? []).map((family) => family.requestId).join('|');
  }

  private caregiverDocRef(record: CaregiverSelectionRecordSortable): string {
    return (
      record.record.babysitter?.docRef ??
      record.record.petsitter?.docRef ??
      record.record.nurse?.docRef ??
      record.record.colf?.docRef ??
      `${record.record.id}`
    );
  }

  onAddToSelection(record: CaregiverSelectionRecordSortable): void {
    this.selectedCaregiver.emit(record);
  }

  trackByCaregiverMarker = (_index: number, marker: CaregiverMapMarker): string =>
    this.caregiverDocRef(marker.record);

  trackByFamilyMarker = (_index: number, marker: FamilyMapMarker): number => marker.family.requestId;

  private updateMapAndCircleOptions(): void {
    if (!this.centerCoordinates) {
      this.mapOptions = null;
      this.circleOptions = null;
      return;
    }
    this.mapOptions = {
      center: this.centerCoordinates,
      mapId: ACTIVE_SEARCH_MAP_ID,
      zoom: this.computeZoom(this.rangeMeters),
    };
    this.circleOptions = {
      center: this.centerCoordinates,
      radius: this.rangeMeters,
      strokeColor: '#3C66A3',
      strokeOpacity: 0.6,
      strokeWeight: 1,
      fillColor: '#3C66A3',
      fillOpacity: 0.05,
    };
  }

  private refreshCenterPin(): void {
    if (!this.centerCoordinates || !this.pinElementCtor) {
      return;
    }
    const signature = `${this.centerCoordinates.lat}:${this.centerCoordinates.lng}`;
    if (signature === this.lastCenterSignature) {
      return;
    }
    this.lastCenterSignature = signature;
    this.centerMarkerPin = new this.pinElementCtor({
      background: CENTER_PIN_COLOR,
      borderColor: '#ffffff',
      glyph: this.buildHouseGlyph(),
      scale: 1.2,
    });
  }

  private buildHouseGlyph(): SVGSVGElement {
    const svgNs = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNs, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '14');
    svg.setAttribute('height', '14');
    const path = document.createElementNS(svgNs, 'path');
    path.setAttribute('fill', '#ffffff');
    path.setAttribute('d', 'M12 2.7 1.5 11.2l1.26 1.55L4 11.6V21a1 1 0 0 0 1 1h4.5a1 1 0 0 0 1-1v-5h3v5a1 1 0 0 0 1 1H19a1 1 0 0 0 1-1v-9.4l1.24 1.15L21.5 11.2Z');
    svg.appendChild(path);
    return svg;
  }

  private computeZoom(rangeMeters: number): number {
    const km = rangeMeters / 1000;
    if (km <= 1) return 14;
    if (km <= 2) return 13;
    if (km <= 5) return 12;
    if (km <= 10) return 11;
    if (km <= 20) return 10;
    if (km <= 30) return 9;
    return 8;
  }

  private buildCaregiverMarkers(records: CaregiverSelectionRecordSortable[]): CaregiverMapMarker[] {
    return (records ?? [])
      .map((record) => {
        const position = this.extractCaregiverPosition(record);
        if (!position) {
          return null;
        }
        return {
          record,
          position,
          pin: this.pinElementCtor ? this.buildCaregiverPin(record.fittingIndex) : null,
        };
      })
      .filter((marker): marker is CaregiverMapMarker => marker !== null);
  }

  private buildAlreadySelectedMarkers(records: CaregiverSelectionRecordSortable[]): CaregiverMapMarker[] {
    return (records ?? [])
      .map((record) => {
        const position = this.extractCaregiverPosition(record);
        if (!position) {
          return null;
        }
        return {
          record,
          position,
          pin: this.pinElementCtor ? this.buildAlreadySelectedPin() : null,
        };
      })
      .filter((marker): marker is CaregiverMapMarker => marker !== null);
  }

  private buildFamilyMarkers(families: NearbyActiveFamilyMarker[]): FamilyMapMarker[] {
    return (families ?? [])
      .filter((family) => this.isValidCoordinate(family.latitude) && this.isValidCoordinate(family.longitude))
      .map((family) => ({
        family,
        position: { lat: family.latitude, lng: family.longitude },
        pin: this.pinElementCtor ? this.buildFamilyPin() : null,
      }));
  }

  private extractCaregiverPosition(record: CaregiverSelectionRecordSortable): google.maps.LatLngLiteral | null {
    const info =
      record.record.babysitter?.info ??
      record.record.petsitter?.info ??
      record.record.nurse?.info ??
      record.record.colf?.info;
    if (!info || !this.isValidCoordinate(info.latitude) || !this.isValidCoordinate(info.longitude)) {
      return null;
    }
    return { lat: info.latitude, lng: info.longitude };
  }

  private isValidCoordinate(value: number | undefined | null): value is number {
    return typeof value === 'number' && !Number.isNaN(value) && value !== 0;
  }

  private buildCaregiverPin(fittingIndex: number): google.maps.marker.PinElement {
    const t = Math.max(0, Math.min(1, (fittingIndex ?? 0) / 100));
    return new this.pinElementCtor!({
      background: this.interpolateCaregiverColor(t),
      borderColor: '#ffffff',
      glyphColor: '#ffffff',
      scale: 0.8 + t * 0.6,
    });
  }

  private buildFamilyPin(): google.maps.marker.PinElement {
    return new this.pinElementCtor!({
      background: FAMILY_PIN_COLOR,
      borderColor: '#ffffff',
      glyphColor: '#ffffff',
      scale: 1,
    });
  }

  private buildAlreadySelectedPin(): google.maps.marker.PinElement {
    return new this.pinElementCtor!({
      background: ALREADY_SELECTED_PIN_COLOR,
      borderColor: '#ffffff',
      glyphColor: '#ffffff',
      scale: 0.8,
    });
  }

  private interpolateCaregiverColor(t: number): string {
    const r = Math.round(CAREGIVER_PIN_FROM.r + (CAREGIVER_PIN_TO.r - CAREGIVER_PIN_FROM.r) * t);
    const g = Math.round(CAREGIVER_PIN_FROM.g + (CAREGIVER_PIN_TO.g - CAREGIVER_PIN_FROM.g) * t);
    const b = Math.round(CAREGIVER_PIN_FROM.b + (CAREGIVER_PIN_TO.b - CAREGIVER_PIN_FROM.b) * t);
    return `rgb(${r}, ${g}, ${b})`;
  }
}
